import { useEffect, useState, useRef, Fragment } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import Badge from 'components/Badge'
import { actions } from 'modules/home/HomeReducer'
import {
  getSpecialtyInput,
  getAd,
  getMediaUrlPSource
} from 'modules/home/HomeSelector'
import { getPartyId, getProfileInfo } from 'modules/auth/AuthSelector'
import { scrollToAnchor } from 'utils'
import {
  iconAdCollapsed,
  iconAdControl,
  iconAdClosed,
  iconToastSuccess,
  iconToastClose
} from 'components/Icons'
import styles from 'components/Ad/Ad.module.sass'

const AdCard = ({
  type,
  position,
  specialty,
  anchor = false,
  viewid,
  badge = false,
  random
}) => {
  const dispatch = useDispatch()
  const [timer, setTimer] = useState(0)
  const [closed, setClosed] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [collapsed, setCollapsed] = useState(false)

  const partyId = useSelector(getPartyId)
  const profileInfo = useSelector(getProfileInfo)
  const specialtyInput = useSelector(getSpecialtyInput)
  const mediaUrlPSource = useSelector(getMediaUrlPSource)
  const { data } = useSelector(getAd)
  const [ads, setAds] = useState([])
  const specIdRef = useRef('')
  const partyIdRef = useRef('')

  const [iframeHeight, setIframeHeight] = useState('450px')
  const [controlMarginTop, setControlMarginTop] = useState('197.5px')
  useEffect(() => {
    setIframeHeight(
      collapsed
        ? ads[0]?.height?.match(/\d+/)[0] / 2 + 'px' || '225px'
        : ads[0]?.height || '450px'
    )
    setControlMarginTop(
      collapsed
        ? ads[0]?.height?.match(/\d+/)[0] / 2 / 2 - 55 / 2 + 'px' || '85px'
        : ads[0]?.height / 2 - 55 / 2 || '197.5px'
    )
  }, [collapsed, ads])

  useEffect(() => {
    if (specialtyInput.value) setAds([])
    let payload = { type, random, specialtyId: specialtyInput.value }
    if (position) payload.position = position

    // only performs ad request when partyId is not empty
    if (partyId !== '') {
      // if it is the trigger from partyId
      if (partyId !== partyIdRef.current) {
        // specIdRef.current would need to get from user profile
        specIdRef.current = profileInfo?.specialtyId || specialtyInput.value
        payload.specialtyId = specIdRef.current
        partyIdRef.current = partyId
        dispatch(actions.requestAd(payload))
      } else if (specIdRef.current !== specialtyInput.value) {
        specIdRef.current = specialtyInput.value
        dispatch(actions.requestAd(payload))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, specialtyInput?.value, partyId])

  useEffect(() => {
    if (!ads.length && data.length) {
      const adFound = data.findLast(ad => ad.random === random)
      if (adFound) setAds([adFound])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length])

  return ads?.map(
    ({
      type,
      clickUrl,
      mediaUrl,
      logo,
      sourceName,
      topRightText,
      title,
      html
    }) => (
      <Fragment key={title}>
        {anchor && type !== 'html' && (
          <p
            style={{
              textAlign: 'center',
              margin: '0 0 16px',
              lineHeight: '18px'
            }}
          >
            <button
              type="button"
              className={[styles.linkbutton, styles.ad_link].join(' ')}
              onClick={() =>
                scrollToAnchor(
                  `article${anchor + 1}`,
                  window.innerWidth <= 1040 ? 165 : 100
                )
              }
            >
              Next article
            </button>
          </p>
        )}
        {badge && (
          <div className={styles.cardHeader}>
            {sourceName && <Badge text={sourceName} icon={logo} />}
            {html?.publicationDoctor && (
              <Badge text={html.publicationDoctor} icon={html.logo} />
            )}
            <p className={styles.category}>
              {html?.topRightText ?? topRightText}
            </p>
          </div>
        )}
        <div
          key={title}
          className={styles.main}
          style={closed ? { border: 'none' } : {}}
        >
          {type === 'html' && (
            <>
              <a href={clickUrl} target="_blank" rel="noreferrer">
                <p className={styles.title}>{html?.headline}</p>
              </a>
              <div className={styles.cardBody}>
                <p className={styles.description}>
                  {html?.teaserText ? html.teaserText + '...' : ''}
                  <a
                    href={clickUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.readmore}
                  >
                    {html.ctaText}
                  </a>
                </p>
              </div>
            </>
          )}
          {type === 'iframe' &&
            closed &&
            (showToast ? (
              <div className={styles.toast}>
                <div style={{ display: 'flex', width: '34px' }}>
                  {iconToastSuccess({
                    style: { width: '34px', height: '34px' }
                  })}
                </div>
                <div style={{ flexDirection: 'column' }}>
                  <h2>The content is now hidden</h2>
                  <p>
                    If you would like to see it, please click{' '}
                    <button
                      data-testid="link-reopen"
                      onClick={() => {
                        setShowToast(false)
                        setClosed(false)
                        clearTimeout(timer)
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setShowToast(false)
                          setClosed(false)
                          clearTimeout(timer)
                        }
                      }}
                    >
                      reopen
                    </button>{' '}
                    or use the expand button.
                  </p>
                </div>
                <span
                  role="button"
                  tabIndex="0"
                  style={{ display: 'flex', width: '36px' }}
                  onClick={() => {
                    setShowToast(false)
                    clearTimeout(timer)
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setShowToast(false)
                      clearTimeout(timer)
                    }
                  }}
                >
                  {iconToastClose({
                    style: { width: '36px', height: '36px' },
                    className: styles.close
                  })}
                </span>
              </div>
            ) : (
              <>
                <span
                  role="button"
                  tabIndex="0"
                  onClick={() => {
                    setClosed(false)
                    clearTimeout(timer)
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setClosed(false)
                      clearTimeout(timer)
                    }
                  }}
                >
                  {iconAdClosed({
                    style: { color: '#08487D' },
                    className: styles.control2,
                    testid: 'button-reopen'
                  })}
                </span>
                <p className={styles.controlText}>This course is hidden.</p>
              </>
            ))}
          {type === 'iframe' && !closed && (
            <>
              <iframe
                src={mediaUrl + mediaUrlPSource}
                title={title}
                className={styles.iframe}
                style={{
                  height: iframeHeight
                }}
              ></iframe>
              {collapsed
                ? iconAdCollapsed({
                    style: { marginTop: controlMarginTop, color: '#08487D' },
                    className: styles.control
                  })
                : iconAdControl({
                    style: { marginTop: controlMarginTop, color: '#08487D' },
                    className: styles.control
                  })}
              <div
                className={styles.control}
                style={{
                  marginTop: controlMarginTop,
                  height: '55px',
                  width: '20px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <button
                  type="button"
                  data-testid="rect-close"
                  aria-label="Hide content"
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 0,
                    padding: 0,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setClosed(true)
                    setShowToast(true)
                    setTimer(setTimeout(() => setShowToast(false), 5000))
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setClosed(true)
                      setShowToast(true)
                      setTimer(setTimeout(() => setShowToast(false), 5000))
                    }
                  }}
                />
                <button
                  type="button"
                  data-testid="rect-resize"
                  aria-label={collapsed ? 'Expand content' : 'Collapse content'}
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 0,
                    padding: 0,
                    cursor: 'pointer'
                  }}
                  onClick={() => setCollapsed(!collapsed)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ')
                      setCollapsed(!collapsed)
                  }}
                />
              </div>
            </>
          )}
        </div>
        {anchor && type !== 'html' && (
          <p
            style={{
              textAlign: 'center',
              margin: '15px 0 0',
              lineHeight: '18px'
            }}
          >
            <button
              type="button"
              className={[styles.linkbutton, styles.ad_link].join(' ')}
              onClick={() =>
                scrollToAnchor(
                  `article${anchor}`,
                  window.innerWidth <= 1040 ? 165 : 100
                )
              }
            >
              Previous article
            </button>
          </p>
        )}
      </Fragment>
    )
  )
}

AdCard.propTypes = {
  type: PropTypes.string,
  position: PropTypes.string,
  specialty: PropTypes.number,
  anchor: PropTypes.number,
  viewid: PropTypes.string,
  badge: PropTypes.bool,
  random: PropTypes.string
}

export default AdCard
